import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Save, Calendar, Clock, PoundSterling, Music, Disc3, Building2, MapPin, Plus, Trash2, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import ImageUpload from '../components/ImageUpload';
import { useAuth } from '../context/AuthContext';

import { API_URL } from '../utils/api';

export default function AddEditEventPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [myClubs, setMyClubs] = useState([]);
  const [myDJs, setMyDJs] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hostType, setHostType] = useState('club'); // 'club', 'dj', or 'organization'
  const [formData, setFormData] = useState({
    club_id: '',
    dj_id: '',
    organizer_name: 'Line Dance Central',
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    price: '',
    image_url: '',
    location: '',
    external_ticket_url: '',
    ticket_widget_code: '',
    ticket_tiers: []  // Array of {name, price, start_date, end_date}
  });
  
  // Ticket tiers state
  const [showTicketTiers, setShowTicketTiers] = useState(false);

  useEffect(() => {
    checkAdminStatus();
    fetchMyClubs();
    fetchMyDJs();
    if (isEdit) {
      fetchEvent();
    }
  }, [id]);

  const checkAdminStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setIsAdmin(data.is_admin || false);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const fetchMyClubs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/my-clubs`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMyClubs(data);
        // If only one club and no DJs, auto-select it
        if (data.length === 1 && !isEdit) {
          setFormData(prev => ({ ...prev, club_id: data[0].club_id }));
        }
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
    }
  };

  const fetchMyDJs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/my-djs`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMyDJs(data);
      }
    } catch (error) {
      console.error('Error fetching DJs:', error);
    }
  };

  const fetchEvent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/events/${id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        // Format date for input
        const eventDate = new Date(data.event_date);
        const ticketTiers = data.ticket_tiers || [];
        setFormData({
          club_id: data.club_id || '',
          dj_id: data.dj_id || '',
          organizer_name: data.organizer_name || 'Line Dance Central',
          title: data.title,
          description: data.description,
          event_date: eventDate.toISOString().split('T')[0],
          event_time: data.event_time,
          price: data.price?.toString() || '',
          image_url: data.image_url || '',
          location: data.location || '',
          external_ticket_url: data.external_ticket_url || '',
          ticket_widget_code: data.ticket_widget_code || '',
          ticket_tiers: ticketTiers.map(tier => ({
            ...tier,
            start_date: tier.start_date ? tier.start_date.split('T')[0] : '',
            end_date: tier.end_date ? tier.end_date.split('T')[0] : ''
          }))
        });
        // Set ticket tiers visibility
        if (ticketTiers.length > 0) {
          setShowTicketTiers(true);
        }
        // Set host type based on existing event
        if (data.is_organization_event) {
          setHostType('organization');
        } else if (data.dj_id) {
          setHostType('dj');
        } else {
          setHostType('club');
        }
      }
    } catch (error) {
      console.error('Error fetching event:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate host selection
    if (hostType === 'club' && !formData.club_id) {
      toast.error('Please select a club for this event');
      return;
    }
    if (hostType === 'dj' && !formData.dj_id) {
      toast.error('Please select a DJ profile for this event');
      return;
    }
    if (hostType === 'organization' && !formData.organizer_name) {
      toast.error('Please enter an organizer name');
      return;
    }
    
    if (!formData.title || !formData.description || !formData.event_date || !formData.event_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = isEdit 
        ? `${API_URL}/api/events/${id}` 
        : `${API_URL}/api/events`;
      
      // Combine date and time for API
      const eventDateTime = new Date(`${formData.event_date}T${formData.event_time}`);
      
      const payload = {
        club_id: hostType === 'club' ? formData.club_id : null,
        dj_id: hostType === 'dj' ? formData.dj_id : null,
        organizer_name: hostType === 'organization' ? formData.organizer_name : null,
        title: formData.title,
        description: formData.description,
        event_date: eventDateTime.toISOString(),
        event_time: formData.event_time,
        price: formData.price ? parseFloat(formData.price) : null,
        image_url: formData.image_url || null,
        location: hostType === 'organization' ? formData.location : null,
        external_ticket_url: hostType === 'organization' && formData.external_ticket_url ? formData.external_ticket_url : null,
        ticket_widget_code: hostType === 'organization' && formData.ticket_widget_code ? formData.ticket_widget_code : null,
        ticket_tiers: hostType === 'organization' && showTicketTiers && formData.ticket_tiers.length > 0 
          ? formData.ticket_tiers.map(tier => ({
              name: tier.name,
              price: parseFloat(tier.price),
              start_date: tier.start_date ? new Date(tier.start_date).toISOString() : null,
              end_date: tier.end_date ? new Date(tier.end_date).toISOString() : null
            }))
          : null
      };

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(isEdit ? 'Event updated!' : 'Event created!');
        navigate('/dashboard?tab=my-events');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save event');
      }
    } catch (error) {
      toast.error('An error occurred');
      console.error('Error saving event:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate time options (every 30 minutes)
  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, '0');
      const minute = m.toString().padStart(2, '0');
      const time24 = `${hour}:${minute}`;
      const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      const ampm = h < 12 ? 'AM' : 'PM';
      const display = `${hour12}:${minute} ${ampm}`;
      timeOptions.push({ value: time24, label: display });
    }
  }

  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="add-edit-event-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                data-testid="back-btn"
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Back
              </Button>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Syne, sans-serif' }}>
                {isEdit ? 'Edit Event' : 'Create New Event'}
              </h1>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-violet-600 hover:bg-violet-500"
              data-testid="save-event-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Event'}
            </Button>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="container mx-auto px-6 lg:px-12 py-8">
        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto space-y-6">
          
          {/* Host Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-6">Event Host</h2>
            
            {myClubs.length === 0 && myDJs.length === 0 && !isAdmin ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 mb-4">You need to create a club or DJ profile first before adding events.</p>
                <div className="flex gap-3 justify-center">
                  <Button onClick={() => navigate('/dashboard/add-club')} className="bg-violet-600 hover:bg-violet-500">
                    <Music className="w-4 h-4 mr-2" />
                    Create a Club
                  </Button>
                  <Button onClick={() => navigate('/dashboard?tab=my-djs')} variant="outline">
                    <Disc3 className="w-4 h-4 mr-2" />
                    Add DJ Profile
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Host Type Toggle */}
                <div>
                  <Label>Host Type *</Label>
                  <div className="flex flex-wrap gap-3 mt-2">
                    {myClubs.length > 0 && (
                      <Button
                        type="button"
                        variant={hostType === 'club' ? 'default' : 'outline'}
                        onClick={() => {
                          setHostType('club');
                          setFormData(prev => ({ ...prev, dj_id: '', organizer_name: 'Line Dance Central' }));
                        }}
                        className={hostType === 'club' ? 'bg-violet-600 hover:bg-violet-500' : ''}
                      >
                        <Music className="w-4 h-4 mr-2" />
                        Club Event
                      </Button>
                    )}
                    {myDJs.length > 0 && (
                      <Button
                        type="button"
                        variant={hostType === 'dj' ? 'default' : 'outline'}
                        onClick={() => {
                          setHostType('dj');
                          setFormData(prev => ({ ...prev, club_id: '', organizer_name: 'Line Dance Central' }));
                        }}
                        className={hostType === 'dj' ? 'bg-violet-600 hover:bg-violet-500' : ''}
                      >
                        <Disc3 className="w-4 h-4 mr-2" />
                        DJ Event
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        type="button"
                        variant={hostType === 'organization' ? 'default' : 'outline'}
                        onClick={() => {
                          setHostType('organization');
                          setFormData(prev => ({ ...prev, club_id: '', dj_id: '' }));
                        }}
                        className={hostType === 'organization' ? 'bg-emerald-600 hover:bg-emerald-500' : 'border-emerald-300 text-emerald-700'}
                        data-testid="organization-event-btn"
                      >
                        <Building2 className="w-4 h-4 mr-2" />
                        Organization Event
                      </Button>
                    )}
                  </div>
                  {isAdmin && hostType === 'organization' && (
                    <p className="text-sm text-emerald-600 mt-2">
                      Organization events don&apos;t require a club or DJ profile. Great for Line Dance Central&apos;s own events.
                    </p>
                  )}
                </div>

                {/* Auto-select host type if user only has one type and is not admin */}
                {!isAdmin && myClubs.length > 0 && myDJs.length === 0 && hostType !== 'club' && setHostType('club')}
                {!isAdmin && myDJs.length > 0 && myClubs.length === 0 && hostType !== 'dj' && setHostType('dj')}

                {/* Club Selection */}
                {hostType === 'club' && myClubs.length > 0 && (
                  <div>
                    <Label htmlFor="club_id">Select Club *</Label>
                    <Select
                      value={formData.club_id}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, club_id: value }))}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="event-club-select">
                        <SelectValue placeholder="Select a club" />
                      </SelectTrigger>
                      <SelectContent>
                        {myClubs.map(club => (
                          <SelectItem key={club.club_id} value={club.club_id}>
                            {club.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-slate-400 mt-1.5">
                      This event will be hosted by the selected club
                    </p>
                  </div>
                )}

                {/* DJ Selection */}
                {hostType === 'dj' && myDJs.length > 0 && (
                  <div>
                    <Label htmlFor="dj_id">Select DJ Profile *</Label>
                    <Select
                      value={formData.dj_id}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, dj_id: value }))}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="event-dj-select">
                        <SelectValue placeholder="Select a DJ profile" />
                      </SelectTrigger>
                      <SelectContent>
                        {myDJs.map(dj => (
                          <SelectItem key={dj.dj_id} value={dj.dj_id}>
                            {dj.stage_name || dj.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-slate-400 mt-1.5">
                      This event will be hosted by your DJ profile
                    </p>
                  </div>
                )}

                {/* Organization Event Fields */}
                {hostType === 'organization' && (
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="organizer_name">Organizer Name *</Label>
                      <Input
                        id="organizer_name"
                        name="organizer_name"
                        value={formData.organizer_name}
                        onChange={handleChange}
                        placeholder="e.g., Line Dance Central"
                        className="mt-1.5"
                        data-testid="organizer-name-input"
                      />
                      <p className="text-sm text-slate-400 mt-1.5">
                        This name will appear as the event host
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="location">Event Location *</Label>
                      <Input
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="e.g., Manchester Conference Centre, Oxford Road"
                        className="mt-1.5"
                        data-testid="event-location-input"
                      />
                      <p className="text-sm text-slate-400 mt-1.5">
                        Full address or venue name
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </motion.div>

          {(myClubs.length > 0 || myDJs.length > 0 || isAdmin) && (
            <>
              {/* Event Details */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
              >
                <h2 className="text-lg font-bold text-slate-900 mb-6">Event Details</h2>
                
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="title">Event Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="e.g., Saturday Night Line Dance Social"
                      required
                      className="mt-1.5"
                      data-testid="event-title-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Describe your event..."
                      required
                      rows={4}
                      className="mt-1.5"
                      data-testid="event-description-input"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Date & Time */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
              >
                <h2 className="text-lg font-bold text-slate-900 mb-6">Date & Time</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="event_date">Event Date *</Label>
                    <div className="relative mt-1.5">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <Input
                        id="event_date"
                        name="event_date"
                        type="date"
                        value={formData.event_date}
                        onChange={handleChange}
                        min={today}
                        required
                        className="pl-10"
                        data-testid="event-date-input"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="event_time">Start Time *</Label>
                    <div className="relative mt-1.5">
                      <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 z-10" />
                      <Select
                        value={formData.event_time}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, event_time: value }))}
                      >
                        <SelectTrigger className="pl-10" data-testid="event-time-select">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeOptions.map(({ value, label }) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Price */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
              >
                <h2 className="text-lg font-bold text-slate-900 mb-6">Pricing & Tickets</h2>
                
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="price">Entry Price (optional)</Label>
                    <div className="relative mt-1.5">
                      <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <Input
                        id="price"
                        name="price"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.price}
                        onChange={handleChange}
                        placeholder="0.00"
                        className="pl-10"
                        data-testid="event-price-input"
                        disabled={showTicketTiers && formData.ticket_tiers.length > 0}
                      />
                    </div>
                    <p className="text-sm text-slate-400 mt-1.5">
                      {showTicketTiers && formData.ticket_tiers.length > 0 
                        ? 'Price is determined by ticket tiers below'
                        : 'Leave blank if the event is free'}
                    </p>
                  </div>
                  
                  {/* Ticket Tiers - Only for Organization events */}
                  {hostType === 'organization' && (
                    <div className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Tag className="w-5 h-5 text-violet-600" />
                          <Label className="text-base font-semibold">Time-Based Pricing Tiers</Label>
                        </div>
                        <Button
                          type="button"
                          variant={showTicketTiers ? "default" : "outline"}
                          size="sm"
                          onClick={() => setShowTicketTiers(!showTicketTiers)}
                        >
                          {showTicketTiers ? 'Enabled' : 'Enable'}
                        </Button>
                      </div>
                      
                      {showTicketTiers && (
                        <div className="space-y-4">
                          <p className="text-sm text-slate-500">
                            Set different prices for different time periods (e.g., Early Bird, Standard, Late). 
                            The current price will display automatically based on the current date.
                          </p>
                          
                          {formData.ticket_tiers.map((tier, index) => (
                            <div key={index} className="bg-slate-50 rounded-lg p-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-slate-700">Tier {index + 1}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const newTiers = formData.ticket_tiers.filter((_, i) => i !== index);
                                    setFormData(prev => ({ ...prev, ticket_tiers: newTiers }));
                                  }}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-xs">Tier Name</Label>
                                  <Input
                                    value={tier.name}
                                    onChange={(e) => {
                                      const newTiers = [...formData.ticket_tiers];
                                      newTiers[index].name = e.target.value;
                                      setFormData(prev => ({ ...prev, ticket_tiers: newTiers }));
                                    }}
                                    placeholder="e.g., Early Bird"
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Price (£)</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={tier.price}
                                    onChange={(e) => {
                                      const newTiers = [...formData.ticket_tiers];
                                      newTiers[index].price = e.target.value;
                                      setFormData(prev => ({ ...prev, ticket_tiers: newTiers }));
                                    }}
                                    placeholder="0.00"
                                    className="mt-1"
                                  />
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-xs">Available From</Label>
                                  <Input
                                    type="date"
                                    value={tier.start_date}
                                    onChange={(e) => {
                                      const newTiers = [...formData.ticket_tiers];
                                      newTiers[index].start_date = e.target.value;
                                      setFormData(prev => ({ ...prev, ticket_tiers: newTiers }));
                                    }}
                                    className="mt-1"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Available Until</Label>
                                  <Input
                                    type="date"
                                    value={tier.end_date}
                                    onChange={(e) => {
                                      const newTiers = [...formData.ticket_tiers];
                                      newTiers[index].end_date = e.target.value;
                                      setFormData(prev => ({ ...prev, ticket_tiers: newTiers }));
                                    }}
                                    className="mt-1"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                ticket_tiers: [
                                  ...prev.ticket_tiers,
                                  { name: '', price: '', start_date: '', end_date: '' }
                                ]
                              }));
                            }}
                            className="w-full"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Pricing Tier
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* External Ticket URL - Only for Organization events */}
                  {hostType === 'organization' && (
                    <div>
                      <Label htmlFor="external_ticket_url">External Ticket Link (optional)</Label>
                      <Input
                        id="external_ticket_url"
                        name="external_ticket_url"
                        type="url"
                        value={formData.external_ticket_url}
                        onChange={handleChange}
                        placeholder="https://www.ticketscandy.com/e/your-event"
                        className="mt-1.5"
                        data-testid="event-external-ticket-url"
                      />
                      <p className="text-sm text-slate-400 mt-1.5">
                        Link to an external ticketing provider. Opens in a popup on your site.
                      </p>
                    </div>
                  )}
                  
                  {/* Ticket Widget Code - Only for Organization events */}
                  {hostType === 'organization' && (
                    <div>
                      <Label htmlFor="ticket_widget_code">Ticket Widget Code (optional)</Label>
                      <textarea
                        id="ticket_widget_code"
                        name="ticket_widget_code"
                        value={formData.ticket_widget_code}
                        onChange={handleChange}
                        placeholder="<script>...</script> or <iframe>...</iframe>"
                        className="mt-1.5 w-full min-h-[120px] px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                        data-testid="event-ticket-widget-code"
                      />
                      <p className="text-sm text-slate-400 mt-1.5">
                        Paste the embed code from TicketsCandy or other providers. This will display the ticket widget directly on the event page.
                        <br />
                        <span className="text-orange-500 font-medium">Note:</span> If both URL and Widget Code are provided, the Widget Code takes priority.
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Event Image */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl shadow-sm p-6 md:p-8"
              >
                <h2 className="text-lg font-bold text-slate-900 mb-6">Event Image</h2>
                <ImageUpload
                  value={formData.image_url}
                  onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
                  label="Upload Event Poster"
                  helpText="Upload an eye-catching image or poster for your event"
                />
              </motion.div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
